import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import { storyTaskStatus, storyTaskSubmit, type StoryTaskStatus, type StoryStatus } from "../lib/api";
import { useHub } from "../hooks/useHub";
import { taskInfo } from "../data/tasks";
import TabBar from "../components/TabBar";
import { useT } from "../i18n";
import { errorMessage } from "../i18n/errors";

const STATUS_ICON: Record<StoryStatus, string> = { approved: "✅", pending: "⏳", none: "○" };

// Личная сдача сторис-задания: инста + галочка → «на проверке» → модератор
// засчитывает. Ниже — прогресс по каждому участнику клана.
function StorySection({ phone, q }: { phone: string; q: string }) {
  const nav = useNavigate();
  const { t } = useT();
  const { hub } = useHub(phone);
  const inClan = !!hub?.clan;

  const [st, setSt] = useState<StoryTaskStatus | null>(null);
  const [insta, setInsta] = useState("");
  const [checked, setChecked] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!inClan) return;
    let alive = true;
    const tick = () =>
      storyTaskStatus()
        .then((r) => {
          if (alive) setSt(r);
        })
        .catch(() => {
          /* следующий тик обновит */
        });
    tick();
    const timer = setInterval(tick, 10000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [inClan]);

  async function submit() {
    setError("");
    if (!insta.trim()) return setError(t("task.story.errNoInsta"));
    setSending(true);
    try {
      await storyTaskSubmit(insta.trim());
      setSt(await storyTaskStatus());
    } catch (e: unknown) {
      setError(errorMessage(e, t));
    } finally {
      setSending(false);
    }
  }

  if (!hub) return null;

  if (!inClan) {
    return (
      <div className="mt-5 bg-card border border-line-2 rounded-[16px] p-4">
        <p className="text-sm text-muted leading-relaxed">{t("task.story.needClan")}</p>
        <button
          type="button"
          onClick={() => nav(`/clan${q}`)}
          className="mt-3 h-11 px-5 rounded-[12px] bg-violet-grad text-white font-bold text-sm active:opacity-90 shadow-violet-btn"
        >
          {t("tab.clans")}
        </button>
      </div>
    );
  }

  const mine = st?.mine.status ?? "none";
  return (
    <div className="mt-5 flex flex-col gap-3.5">
      {/* Мой статус / форма сдачи */}
      {mine === "approved" ? (
        <div className="rounded-[16px] bg-success/[0.12] border border-success/25 px-4 py-3.5 text-sm font-semibold text-success">
          {t("task.story.approved")}
        </div>
      ) : mine === "pending" ? (
        <div className="rounded-[16px] bg-violet-t12 border border-violet/40 px-4 py-3.5">
          <p className="text-sm font-semibold text-violet-lighter">{t("task.story.pending")}</p>
          <p className="text-[13px] text-muted mt-1">
            {t("task.story.pendingHint")} {st?.mine.instagram ? `@${st.mine.instagram}` : ""}
          </p>
        </div>
      ) : (
        <div className="bg-card border border-line-2 rounded-[16px] p-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-white/80">{t("task.story.instaLabel")}</span>
            <input
              value={insta}
              onChange={(e) => setInsta(e.target.value.replace(/\s/g, ""))}
              placeholder={t("task.story.instaPh")}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="w-full bg-input text-black rounded-[12px] px-3.5 py-2.5 text-sm placeholder:text-grey/70 outline-none focus:ring-2 focus:ring-violet"
            />
          </label>
          <label className="flex items-start gap-2.5 text-[13px] text-white/85 leading-[1.4]">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#7C5CFC]"
            />
            {t("task.story.checkbox")}
          </label>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button
            type="button"
            onClick={submit}
            disabled={sending || !checked || !insta.trim()}
            className="w-full h-12 rounded-[12px] bg-violet-grad text-white font-extrabold text-sm active:opacity-90 disabled:opacity-50 transition-opacity shadow-violet-btn"
          >
            {sending ? t("task.story.submitting") : t("task.story.submit")}
          </button>
        </div>
      )}

      {/* Прогресс клана: кто уже выложил */}
      {st && st.total > 0 && (
        <div className="bg-card border border-line-2 rounded-[16px] p-4">
          <div className="text-sm font-bold text-white mb-2.5">
            {t("task.story.progress", { done: st.approved, total: st.total })}
          </div>
          <div className="flex flex-col gap-2">
            {st.members.map((m, i) => (
              <div key={i} className="flex items-center gap-2.5 text-[13px]">
                <span className="w-5 text-center">{STATUS_ICON[m.status]}</span>
                <span className="flex-1 text-white/85 truncate">{m.nickname || "—"}</span>
                <span className={m.status === "approved" ? "text-success" : m.status === "pending" ? "text-violet-lighter" : "text-muted2"}>
                  {t(`task.story.st.${m.status}`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Детальная страница кланового задания: фото (пока плейсхолдер) + описание.
export default function TaskDetail() {
  const nav = useNavigate();
  const { t } = useT();
  const { key = "" } = useParams();
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";
  const q = `?phone=${encodeURIComponent(phone)}`;

  const info = taskInfo(key);
  // Неизвестное задание (например, добавили через админку без детальной страницы) — на «Кланы».
  if (!info) return <Navigate to={`/clan${q}`} replace />;

  return (
    <div className="min-h-dvh max-w-md mx-auto pb-28">
      {/* Фото задания (или градиент-плейсхолдер с эмодзи) */}
      <div className="relative h-[260px] overflow-hidden">
        {info.image ? (
          <img src={info.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <>
            <div className="absolute inset-0" style={{ background: info.placeholder }} />
            <span className="absolute inset-0 flex items-center justify-center text-[96px] opacity-[0.22] select-none">
              {info.emoji}
            </span>
          </>
        )}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-bg to-transparent" />
        <button
          type="button"
          onClick={() => nav(`/clan${q}`)}
          aria-label={t("prizes.back")}
          className="absolute top-4 left-4 w-9 h-9 rounded-full bg-black/40 backdrop-blur flex items-center justify-center active:bg-black/60 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 6l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="absolute top-4 right-4 text-[11px] font-bold text-white bg-black/40 backdrop-blur px-2.5 py-1 rounded-lg">
          {t("task.header")}
        </span>
      </div>

      <div className="px-[22px] -mt-2 relative">
        <h1 className="text-[24px] font-extrabold tracking-tight leading-tight">{t(info.titleKey)}</h1>
        <p className="text-[15px] text-white/80 leading-[1.6] mt-3">{t(info.descKey)}</p>

        {info.key === "logo_story" && phone && <StorySection phone={phone} q={q} />}

        {/* Награда за все задания */}
        <div className="mt-5 rounded-[16px] border border-gold/40 bg-gold/[0.08] px-4 py-3.5 text-[13px] leading-[1.5] text-gold-light font-semibold">
          {t("stations.battle.prize")}
        </div>
      </div>

      {phone && <TabBar active="clans" phone={phone} />}
    </div>
  );
}
